import React, { memo } from "react";
import PropTypes from "prop-types";

// components
import Loading from "UIComponents/Loading/Loading";

// third-party
import _ from "lodash";
import { Editor } from "@monaco-editor/react";

// hooks
import { useFetchQuery } from "Hooks";

// utils
import { getMarkdownToJsonCode, getJsonToMarkdownCode } from "Utils/AIUtils";

// modules
import { getAttachmentModalityQuery } from "UIComponents/AIChat/modules/AIChatQueries";

// scss
import classes from "./CodeEditorWrapper.scss";

const codeEditorOptions = mode => {
  return {
    minimap: { enabled: false },
    fontSize: 14,
    readOnly: mode === "view",
    wordWrap: "on",
    wrappingStrategy: "advanced",
    scrollBeyondLastLine: false,
  };
};

const styles = {
  loadingContainerStyle: {
    height: "100%",
    backgroundColor: "var(--decorative-foreground-neutral)",
  },
};

const parseCodeEditorData = data => {
  const node = _.get(data, "node", {});
  const code = _.get(node, "modalityAttachment.content", "");
  const metadata = _.get(node, "metadata", {});
  const language = _.get(node, "modalityAttachment.metadata.language", "");

  return {
    code,
    language,
    metadata,
  };
};

const CodeEditorWrapper = memo(props => {
  const { code, language, onChange, attachmentId, mode } = props;

  const { data, loading } = useFetchQuery({
    query: getAttachmentModalityQuery,
    variables: { id: attachmentId },
    parseData: parseCodeEditorData,
    skipCondition:
      _.isEmpty(attachmentId) || _.startsWith(attachmentId, "NEW_"),
    fetchPolicy: "cache-and-network",
  });

  const editorCode = !_.isNil(code) ? code : _.get(data, "code");
  const { code: parsedCode } = getMarkdownToJsonCode(editorCode);

  const editorLanguage = language || _.get(data, "language");

  const options = codeEditorOptions(mode);

  const onCodeChange = value => {
    const newValue = getJsonToMarkdownCode({ code: value, language });
    onChange(newValue);
  };

  if (loading && _.isEmpty(editorCode)) {
    return (
      <div className={classes.codeEditorContainer}>
        <Loading containerStyle={styles.loadingContainerStyle} />
      </div>
    );
  }

  return (
    <div className={classes.codeEditorContainer}>
      <Editor
        key={mode}
        value={parsedCode}
        language={editorLanguage}
        className={classes.editorContainer}
        loading={<Loading containerStyle={styles.loadingContainerStyle} />}
        onChange={onCodeChange}
        options={options}
        theme={"vs-dark"}
      />
    </div>
  );
});

CodeEditorWrapper.displayName = "CodeEditorWrapper";

CodeEditorWrapper.propTypes = {
  code: PropTypes.string,
  language: PropTypes.string,
  onChange: PropTypes.func,
  attachmentId: PropTypes.string,
};

CodeEditorWrapper.defaultProps = {
  code: "",
  language: "javascript",
  onChange: () => {},
  attachmentId: "",
};

export default CodeEditorWrapper;
